/**
 * Shared vocabulary for the picked-annotations widget.
 *
 * "Show only these annotations" needs no new filter type and no backend work:
 * getValuePathQuery casts any valuePath whose last segment is `_id` to an
 * ObjectId, so a plain valuePathType filter on `_id`, against the annotations
 * collection, is already "these exact annotations".
 */
export const PICKED_ANNOTATIONS_FILTER_ID = "tempPickedAnnotations__id";

export const buildPickedAnnotationsFilter = (annotationIds) => ({
  filterId: PICKED_ANNOTATIONS_FILTER_ID,
  values: annotationIds.map((value) => ({ value })),
  target: { filterType: "valuePathType", valuePath: "_id" },
});

/** The fields Elastic searches — the same two the Annotation Explorer page uses. */
export const ANNOTATION_SEARCH_CONFIG = {
  fields: ["annotations.fragment.allText", "contentEnhancedText"],
};

/** Rich-text blocks to translate, copied from the Annotation Explorer widget. */
export const ANNOTATION_TRANSLATION_CONFIG = {
  customBlocks: [
    { comp: "TitleInput", props: {}, valuePath: "main.title" },
    { comp: "LexicalTextEditor", props: {}, valuePath: "annotations.fragment" },
  ],
};

/**
 * What we store per pick: the id, plus enough text to show the chosen
 * annotation in the config panel (and in the widget's own "no longer
 * available" note) WITHOUT a second fetch. An annotation that is later deleted
 * or re-annotated leaves the id unresolvable — the snippet is what lets the
 * widget still say WHICH one went missing.
 */
export const toPickedEntry = (anno) => ({
  value: String(anno._id),
  display: annotationSnippet(anno),
  contentType: anno?.meta?.contentType,
});

/**
 * The annotation's own words — what the rendered card shows, and the only thing
 * you can actually recognise a quote by.
 *
 * `annotations.observation.title` (the AI's 3-7 word label, e.g. "Anti-child-
 * marriage clarity") is the FALLBACK, not the lead: it describes the quote
 * rather than being it, so two different quotes about the same thing look
 * identical in a picker. Only annotations with no fragment text fall back to it.
 */
export const annotationSnippet = (anno, maxLength = 240) => {
  // Trim BEFORE choosing: a whitespace-only allText is truthy, so a plain
  // `a || b` chain would pick it and then render as empty, never reaching
  // the title.
  const text = [
    anno?.annotations?.fragment?.allText,
    anno?.annotations?.observation?.title,
  ].map((v) => (v || "").trim()).find(Boolean);
  if (!text) return "(no text)";
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
};
